import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Button, Select, Space, message, Spin } from "antd";
import { UTCDatePicker, UTCTimePicker } from "../../../../components/DateTimeInput";
import { useScheduleEditor } from "../context/ScheduleEditorContext";
import enhancedApi from "../../../../api/enhancedApi";
import HourMinuteInput from "../../../../components/TimeInput/HourMinuteInput";
import dayjs from "dayjs";
import { handleFlightDateChange } from "../../../../utils/timezoneUtils";
import { formatDuration } from "../../../../utils/timeUtils";
import { parsePositiveMinutes, truncateIsoToMinute } from "../../../../utils/staBlockTimeAlignment";
import StaFollowBlockTimeService from "../services/StaFollowBlockTimeService";
import { STC_TYPE_OPTIONS, DEFAULT_STC_TYPE } from "../../../../constants/flightConstants";

/**
 * 航班表单组件 - 用于编辑/新增航班
 */
const FlightForm = ({ flight, visible, onCancel, onSubmit }) => {
    const { state } = useScheduleEditor();
    const { schedule } = state;
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [airportOptions, setAirportOptions] = useState([]);
    const [loadingAirports, setLoadingAirports] = useState(false);
    const [flightRouteOptions, setFlightRouteOptions] = useState([]);
    const [loadingFlightRoutes, setLoadingFlightRoutes] = useState(false);
    const [routesCache, setRoutesCache] = useState({}); // 缓存航路数据

    // 获取机场数据
    useEffect(() => {
        const fetchAirports = async () => {
            try {
                setLoadingAirports(true);

                // 先检查全局缓存中是否已有机场数据
                if (window.cachedAirports && Array.isArray(window.cachedAirports) && window.cachedAirports.length > 0) {
                    const airportsObj = {};
                    window.cachedAirports.forEach((airport) => {
                        airportsObj[airport.iata_code] = airport;
                    });

                    setAirportOptions(
                        window.cachedAirports.map((airport) => ({
                            value: airport.iata_code,
                            label: `${airport.iata_code}${airport.icao_code ? ` - ${airport.icao_code}` : ""}${airport.airport_name ? ` (${airport.airport_name})` : ""}`,
                        })),
                    );
                    setLoadingAirports(false);
                    return;
                }

                // 如果没有缓存，则请求数据
                const response = await enhancedApi.masterData.airport.getAirportList();
                // 从 response.data.items 提取数据
                const airports = response?.data?.items || [];

                if (airports.length > 0) {
                    // 缓存到全局
                    window.cachedAirports = airports;

                    const airportsObj = {};
                    airports.forEach((airport) => {
                        airportsObj[airport.iata_code] = airport;
                    });

                    setAirportOptions(
                        airports.map((airport) => ({
                            value: airport.iata_code,
                            label: `${airport.iata_code}${airport.icao_code ? ` - ${airport.icao_code}` : ""}${airport.airport_name ? ` (${airport.airport_name})` : ""}`,
                        })),
                    );
                }
            } catch (error) {
                console.error("获取机场数据失败:", error);
            } finally {
                setLoadingAirports(false);
            }
        };

        if (visible) {
            fetchAirports();
        }
    }, [visible]);

    // 处理起飞时间变化
    const handleStdChange = (time, timeString) => {
        if (!time) return;

        // 更新航班日期与STD保持一致
        form.setFieldValue("flight_date", time);

        const blockTimeMinutes = form.getFieldValue("block_time_minutes");
        if (!blockTimeMinutes) return;

        // 计算新的到达时间
        const newSta = time.add(blockTimeMinutes, "minute");
        form.setFieldValue("sta", newSta);
    };

    // 处理到达时间变化
    const handleStaChange = (time) => {
        if (!time) return;

        // 获取当前表单值
        const blockTimeMinutes = form.getFieldValue("block_time_minutes");
        const currentStd = form.getFieldValue("std");
        const currentFlightDate = form.getFieldValue("flight_date");

        if (!blockTimeMinutes) {
            message.warning("请先设置轮档时间");
            return;
        }

        // 关键思路：
        // 1. 用户修改的只是到达时间的小时和分钟部分
        // 2. 根据到达时间和轮档时间，反向计算起飞时间
        // 3. 智能判断日期关系，处理跨天情况

        // 提取用户选择的时间部分
        const selectedHour = time.hour();
        const selectedMinute = time.minute();
        const selectedSecond = time.second() || 0;

        // 判断日期基准
        let staDate;
        if (currentStd && currentFlightDate) {
            // 如果已有起飞时间和航班日期，则使用原航班日期作为基准
            const stdDayjs = dayjs.utc(currentStd);

            // 判断用户选择的到达时间应该在哪一天
            // 1. 先计算时间差
            const stdHour = stdDayjs.hour();
            const stdMinute = stdDayjs.minute();
            const timeDiffMinutes = (selectedHour - stdHour) * 60 + (selectedMinute - stdMinute);

            if (timeDiffMinutes < 0) {
                // 如果到达时间早于起飞时间，默认是第二天
                staDate = stdDayjs.add(1, "day").startOf("day");
            } else if (timeDiffMinutes < blockTimeMinutes) {
                // 时间差小于轮档时间，判断是否跨天
                // 计算期望的到达时间
                const expectedArrival = stdDayjs.add(blockTimeMinutes, "minute");
                const expectedHour = expectedArrival.hour();
                const expectedMinute = expectedArrival.minute();

                const diffFromExpected = Math.abs(selectedHour * 60 + selectedMinute - (expectedHour * 60 + expectedMinute));

                if (diffFromExpected > 720) {
                    // 如果差距超过12小时，认为是跨天
                    staDate = stdDayjs.add(1, "day").startOf("day");
                } else {
                    staDate = stdDayjs.startOf("day");
                }
            } else {
                // 时间差正常或大于轮档时间，使用同一天
                staDate = stdDayjs.startOf("day");
            }
        } else if (time) {
            // 如果没有航班日期，但有到达时间，使用到达时间的日期
            staDate = dayjs.utc(time).startOf("day");
        } else {
            // 兜底使用当前日期
            staDate = dayjs.utc().startOf("day");
        }

        // 构建完整的到达时间（包含正确的日期）
        const newSta = staDate.hour(selectedHour).minute(selectedMinute).second(selectedSecond);

        // 根据到达时间和轮档时间，反向计算起飞时间
        // 1. 计算起飞的小时和分钟
        let departureHour = selectedHour;
        let departureMinute = selectedMinute;
        let totalMinutes = departureHour * 60 + departureMinute - blockTimeMinutes;

        // 2. 处理可能的跨天情况（起飞时间在前一天）
        let stdDate = staDate;
        if (totalMinutes < 0) {
            // 需要往前推一天
            stdDate = staDate.subtract(1, "day");
            totalMinutes += 24 * 60; // 加上一天的分钟数
        }

        // 3. 重新计算小时和分钟
        departureHour = Math.floor(totalMinutes / 60);
        departureMinute = totalMinutes % 60;

        // 4. 创建起飞时间
        const newStd = stdDate.hour(departureHour).minute(departureMinute).second(selectedSecond);

        // 5. 更新表单
        form.setFieldValue("std", newStd);
        form.setFieldValue("flight_date", newStd); // 航班日期与STD保持一致
        form.setFieldValue("sta", newSta);

        // 检查是否是跨天情况并通知用户
        const daysDiff = newSta.diff(newStd, "day", true);
        if (daysDiff >= 1) {
            message.info(`已调整航班时间：起飞时间${newStd.format("MM-DD HH:mm")}，到达时间${newSta.format("MM-DD HH:mm")}，跨天${Math.floor(daysDiff)}天`);
        }
    };

    // 处理航班日期变更
    const handleFlightDateFormChange = (date) => {
        if (!date || !date.isValid()) return;

        try {
            // 获取当前表单值
            const currentValues = form.getFieldsValue();

            // 使用航班日期编辑处理函数处理日期变更
            if (currentValues.std && currentValues.sta) {
                // 安全获取ISO格式的时间字符串
                const getISOString = (timeObj) => {
                    try {
                        // 处理dayjs对象
                        if (typeof timeObj === "object") {
                            // 如果是dayjs对象
                            if (timeObj.isValid && typeof timeObj.isValid === "function" && timeObj.isValid()) {
                                return timeObj.format();
                            }
                            // 如果是Date对象
                            if (timeObj instanceof Date) {
                                return dayjs(timeObj).format();
                            }
                        }
                        // 如果是字符串，尝试解析
                        if (typeof timeObj === "string") {
                            const parsed = dayjs(timeObj);
                            if (parsed.isValid()) {
                                return timeObj; // 已经是字符串格式，直接返回
                            }
                        }
                        // 兜底：使用当前时间
                        console.warn("无法正确格式化时间对象，使用当前时间:", timeObj);
                        return dayjs().format();
                    } catch (err) {
                        console.error("时间对象格式化错误:", err);
                        return dayjs().format();
                    }
                };

                // 构建一个临时航班对象用于处理
                const tempFlight = {
                    std: getISOString(currentValues.std),
                    sta: getISOString(currentValues.sta),
                    flight_date: getISOString(currentValues.flight_date), // 使用当前表单中的flight_date
                };

                // 使用handleFlightDateChange处理日期变更
                const updatedFlight = handleFlightDateChange(tempFlight, date.format("YYYY-MM-DD"));

                // 更新表单值
                if (updatedFlight.std && updatedFlight.sta) {
                    form.setFieldsValue({
                        std: dayjs.utc(updatedFlight.std),
                        sta: dayjs.utc(updatedFlight.sta),
                        flight_date: dayjs.utc(updatedFlight.flight_date || updatedFlight.std), // 保持一致
                    });
                }
            } else {
                // 如果没有std/sta，只更新flight_date
                form.setFieldValue("flight_date", date);
            }
        } catch (error) {
            console.error("处理航班日期变更失败:", error);
            message.error("处理航班日期变更失败");
        }
    };

    // 处理表单提交
    const handleSubmit = async (values) => {
        try {
            setLoading(true);

            // 【航段航路BH优化】校验航路是否有效
            if (values.flight_route_id) {
                const selectedRoute = routesCache[values.flight_route_id];
                if (!selectedRoute) {
                    message.error("选择的航路不存在，请重新选择有效航路", 6);
                    setLoading(false);
                    return;
                }
                if (selectedRoute.is_active === false) {
                    message.error(`航路"${selectedRoute.route_name}"已失效，无法保存。请选择其他有效航路`, 6);
                    setLoading(false);
                    return;
                }
            }

            // 检查是否有id，如果没有则是新增
            const isNew = !flight || !flight.id;

            // 确保数据类型正确
            if (values.ground_time_minutes !== undefined && values.ground_time_minutes !== null) {
                values.ground_time_minutes = parseInt(values.ground_time_minutes, 10);
            }

            if (values.block_time_minutes !== undefined && values.block_time_minutes !== null) {
                values.block_time_minutes = parseInt(values.block_time_minutes, 10);
            }

            // 设置最小轮档时间，如果编辑航班时没有传入这个值，则使用实际轮档时间作为默认值
            if (values.min_block_time_minutes === undefined || values.min_block_time_minutes === null) {
                values.min_block_time_minutes = flight?.min_block_time_minutes || values.block_time_minutes;
            }

            // 处理ISO时间格式
            if (values.std && typeof values.std === "object" && values.std.isValid && values.std.isValid()) {
                values.std = values.std.utc().format();
            }

            if (values.sta && typeof values.sta === "object" && values.sta.isValid && values.sta.isValid()) {
                values.sta = values.sta.utc().format();
            }

            // 确保flight_date与std保持完全一致 (包含时间部分)
            if (values.std) {
                values.flight_date = values.std;
            } else if (values.flight_date && typeof values.flight_date === "object" && values.flight_date.isValid && values.flight_date.isValid()) {
                values.flight_date = values.flight_date.utc().format();
            }

            // 生成随机临时ID或使用现有ID
            if (isNew) {
                values.id = `temp_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
            } else {
                values.id = flight.id;
            }

            // 必要时确保有schedule_id
            if (schedule && schedule.id) {
                values.schedule_id = schedule.id;
            }

            // 添加航路名称信息（用于表格显示）
            if (values.flight_route_id && routesCache[values.flight_route_id]) {
                const route = routesCache[values.flight_route_id];
                values.flight_route_name = route.route_name;
                values.flight_route_block_time_minutes = route.block_time_minutes;
                values.min_block_time_minutes = route.block_time_minutes;
                values.block_time_minutes = route.block_time_minutes;
            } else {
                // 如果未选择航路，清空相关字段
                values.flight_route_id = null;
                values.flight_route_name = null;
                values.flight_route_block_time_minutes = null;
            }

            values.additional_info = flight?.additional_info ? { ...flight.additional_info } : values.additional_info;
            if (flight?.additional_info?.flight_route) {
                values.additional_info = {
                    ...values.additional_info,
                    flight_route: { ...flight.additional_info.flight_route },
                };
            }

            const mergedFlight = { ...flight, ...values };
            const stdChanged =
                Boolean(truncateIsoToMinute(flight?.std)) &&
                Boolean(truncateIsoToMinute(values.std)) &&
                truncateIsoToMinute(flight.std) !== truncateIsoToMinute(values.std);
            const routeChanged = String(values.flight_route_id || "") !== String(flight?.flight_route_id || "");
            if (stdChanged) {
                const next = StaFollowBlockTimeService.applyStdChangeAbandoningManual(mergedFlight, values.std);
                values.sta = next.sta;
                values.additional_info = next.additional_info;
            } else if (routeChanged && values.flight_route_id) {
                const next = StaFollowBlockTimeService.applyRouteBlockTimeStaPolicy(mergedFlight, parsePositiveMinutes(values.block_time_minutes));
                values.sta = next.sta;
                values.additional_info = next.additional_info;
                values.block_time_minutes = next.block_time_minutes;
                values.min_block_time_minutes = next.min_block_time_minutes;
                values.flight_route_block_time_minutes = next.flight_route_block_time_minutes;
            }

            // 调用onSubmit回调
            onSubmit(values);
        } catch (error) {
            console.error("保存航班错误:", error);
        } finally {
            setLoading(false);
        }
    };

    // 获取航路数据
    useEffect(() => {
        const fetchFlightRoutes = async () => {
            if (!flight || !flight.departure_airport || !flight.arrival_airport) {
                setFlightRouteOptions([]);
                setRoutesCache({});
                return;
            }

            try {
                setLoadingFlightRoutes(true);

                // 【航段航路BH优化】一次性获取所有航路（有效+失效），在前端进行筛选
                // 不传is_active参数，后端返回所有航路
                const response = await enhancedApi.flightRoute.getRoutesByLeg(flight.departure_airport, flight.arrival_airport);
                const routes = response?.data || [];

                // 缓存所有航路数据（包括失效的，以便显示失效航路信息）
                const cache = {};
                routes.forEach((route) => {
                    cache[route.id] = route;
                });
                setRoutesCache(cache);

                // 筛选出有效航路，再筛选适配当前飞机的航路
                const aircraftReg = flight.aircraft_reg;
                const activeRoutes = routes.filter((r) => r.is_active !== false); // 有效航路
                const compatibleRoutes = activeRoutes.filter((route) => {
                    // 如果航路没有设置适配飞机，则认为所有飞机都可以使用
                    if (!route.compatible_aircraft || route.compatible_aircraft.length === 0) {
                        return true;
                    }
                    // 如果航班没有分配飞机，显示所有航路
                    if (!aircraftReg) {
                        return true;
                    }
                    // 检查当前飞机是否在适配列表中
                    return route.compatible_aircraft.includes(aircraftReg);
                });

                // 【航段航路BH优化】构建航路选项，包含详细的悬停提示信息
                const routeOptions = compatibleRoutes.map((route) => {
                    // 构建tooltip提示内容
                    const blockTimeMinutes = route.block_time_minutes;
                    const formattedBlockTime = blockTimeMinutes ? formatDuration(blockTimeMinutes, "h_m") : "N/A";
                    const tooltipParts = [`航路: ${route.route_name}`, `轮挡时间: ${formattedBlockTime}`];

                    // 添加标签信息
                    if (route.tags && route.tags.length > 0) {
                        tooltipParts.push(`标签: ${route.tags.join(", ")}`);
                    }

                    // 添加适配飞机信息
                    if (route.compatible_aircraft && route.compatible_aircraft.length > 0) {
                        tooltipParts.push(`适配飞机: ${route.compatible_aircraft.join(", ")}`);
                    } else {
                        tooltipParts.push(`适配飞机: 所有`);
                    }

                    // 添加航路类型
                    if (route.route_type) {
                        tooltipParts.push(`类型: ${route.route_type}`);
                    }

                    return {
                        value: route.id,
                        label: `${route.route_name} (${formattedBlockTime})${route.is_default ? " [默认]" : ""}`,
                        title: tooltipParts.join(" | "), // 悬停时显示的详细信息
                    };
                });

                // 【航段航路BH优化】检查当前航班的航路是否失效
                let finalOptions = routeOptions;
                if (flight.flight_route_id && !routeOptions.find((opt) => opt.value === flight.flight_route_id)) {
                    // 当前航路不在有效列表中，说明航路已失效
                    // 尝试从缓存中获取失效航路的信息
                    const inactiveRoute = cache[flight.flight_route_id];
                    if (inactiveRoute) {
                        // 添加失效航路到选项列表，但标记为失效
                        finalOptions = [
                            {
                                value: inactiveRoute.id,
                                label: `⚠️ ${inactiveRoute.route_name} (已失效，请重新选择)`,
                                title: `该航路已失效（is_active=false），无法继续使用，请选择其他有效航路`,
                                disabled: true, // 禁用该选项，防止重新选择失效航路
                            },
                            ...routeOptions,
                        ];

                        // 提示用户航路已失效
                        message.warning(`当前航路"${inactiveRoute.route_name}"已失效，请重新选择有效航路`, 6);
                    } else {
                        // 如果缓存中也没有，说明航路可能被删除了
                        // 尝试从flight对象中获取航路名称，提供更友好的提示
                        const routeDisplayName = flight.flight_route_name || `${flight.departure_airport}-${flight.arrival_airport}航路`;
                        const routeInfo = flight.flight_route_name ? `"${flight.flight_route_name}"` : `(${flight.departure_airport}-${flight.arrival_airport})`;

                        // 在下拉列表中显示已删除的航路信息
                        finalOptions = [
                            {
                                value: flight.flight_route_id,
                                label: `❌ ${routeDisplayName} (已删除，请重新选择)`,
                                title: `该航路已不存在或已被删除，请选择其他航路`,
                                disabled: true,
                            },
                            ...routeOptions,
                        ];

                        message.error(`当前关联的航路${routeInfo}已不存在或已被删除，请重新选择`, 6);
                    }
                }

                setFlightRouteOptions(finalOptions);

                // 如果有飞机但筛选后的航路数量少于有效航路总数，提示用户
                if (aircraftReg && compatibleRoutes.length < activeRoutes.length) {
                    const incompatibleCount = activeRoutes.length - compatibleRoutes.length;
                    console.info(`飞机 ${aircraftReg} 有 ${incompatibleCount} 个航路不适配，已自动筛选`);
                }
            } catch (error) {
                console.error("获取航路数据失败:", error);
                message.error("获取航路数据失败");
            } finally {
                setLoadingFlightRoutes(false);
            }
        };

        if (visible) {
            fetchFlightRoutes();
        }
    }, [visible, flight, enhancedApi.flightRoute, message]);

    // 当flight变化时重置表单
    useEffect(() => {
        if (visible && flight) {
            form.resetFields();

            // 处理轮档时间字段 - 确保表单中使用block_time_minutes，值从min_block_time_minutes获取（如果没有则使用默认值）
            const formValues = { ...flight };

            // 如果flight中只有min_block_time_minutes而没有block_time_minutes，将min作为block的默认值
            if (formValues.min_block_time_minutes !== undefined && (formValues.block_time_minutes === undefined || formValues.block_time_minutes === null)) {
                formValues.block_time_minutes = formValues.min_block_time_minutes;
            }

            // 使用setFieldsValue将最新的flight值设置到表单
            form.setFieldsValue(formValues);
        }
    }, [visible, flight, form]);

    return (
        <Modal title={flight && flight.id ? "编辑航班" : "添加航班"} open={visible} onCancel={onCancel} onOk={() => form.submit()} confirmLoading={loading} maskClosable={false} destroyOnHidden width={800}>
            <Form form={form} layout="vertical" autoComplete="off" onFinish={handleSubmit}>
                <div className="form-inline-group">
                    <Form.Item name="flight_number" label="航班号" rules={[{ required: true, message: "请输入航班号" }]}>
                        <Input placeholder="如: CA123" maxLength={7} />
                    </Form.Item>

                    <Form.Item name="stc_type" label="航班性质">
                        <Select placeholder="选择航班性质" options={STC_TYPE_OPTIONS} />
                    </Form.Item>

                    {/* 不能编辑 */}
                    <Form.Item name="departure_airport" label="出发机场" rules={[{ required: true, message: "请选择出发机场" }]}>
                        <Select showSearch placeholder="选择出发机场" options={airportOptions} disabled={true} loading={loadingAirports} filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())} />
                    </Form.Item>

                    <Form.Item name="arrival_airport" label="到达机场" rules={[{ required: true, message: "请选择到达机场" }]}>
                        <Select showSearch placeholder="选择到达机场" options={airportOptions} disabled={true} loading={loadingAirports} filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())} />
                    </Form.Item>

                    <Form.Item name="flight_date" label="航班日期 (UTC)" rules={[{ required: true, message: "请选择航班日期" }]} tooltip="航班的计划日期 (修改日期会同步更新STD和STA，保持跨天关系)">
                        <UTCDatePicker style={{ width: "100%" }} format="YYYY-MM-DD" placeholder="选择航班日期" onChange={handleFlightDateFormChange} />
                    </Form.Item>

                    <Form.Item name="block_time_minutes" label="轮档时间" rules={[{ required: true, message: "请输入轮档时间" }]} tooltip="轮档时间来自航路主数据，只读；选择航路时自动更新">
                        <HourMinuteInput disabled style={{ opacity: 0.85 }} />
                    </Form.Item>

                    <Form.Item name="std" label="计划起飞时间 (UTC)" rules={[{ required: true, message: "请选择计划起飞时间" }]} tooltip="修改起飞时间会自动更新航班日期和到达时间">
                        <UTCTimePicker style={{ width: "100%" }} format="HH:mm" onChange={handleStdChange} showDate={true} />
                    </Form.Item>

                    <Form.Item name="sta" label="计划到达时间 (UTC)" rules={[{ required: true, message: "请选择计划到达时间" }]} tooltip="到达时间的日期会根据轮档时间自动计算，无需手动修改">
                        <UTCTimePicker style={{ width: "100%" }} format="HH:mm" onChange={handleStaChange} showDate={true} disableDateEdit={true} />
                    </Form.Item>

                    {/* 【航段航路BH优化】航路设为必填，确保每个航班都关联航路 */}
                    <Form.Item name="flight_route_id" label="航路" rules={[{ required: false }]} tooltip="为航班绑定航路，航路中的轮档时间将作为最小轮档时间。如果没有可用航路，可以留空。">
                        <Select
                            showSearch
                            placeholder="选择航路（可选）"
                            options={flightRouteOptions}
                            loading={loadingFlightRoutes}
                            allowClear={true}
                            filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                            onChange={(value) => {
                                if (value && routesCache[value]) {
                                    // 从缓存中获取航路数据
                                    const route = routesCache[value];
                                    const routeBlock = route.block_time_minutes; // 统一使用 block_time_minutes
                                    if (route && routeBlock) {
                                        // 更新最小与实际轮档时间
                                        form.setFieldValue("min_block_time_minutes", routeBlock);
                                        form.setFieldValue("block_time_minutes", routeBlock);

                                        const keepManualSta = StaFollowBlockTimeService.shouldKeepManualStaOnRouteChange(flight, routeBlock);
                                        if (keepManualSta) {
                                            message.success(`已应用航路 ${route.route_name} 的轮档时间: ${routeBlock}分钟。手填到达保持不变。`);
                                            return;
                                        }

                                        // 根据新的轮档时间调整STD/STA：优先保持STD不变，更新STA（若无STD则反推STD）
                                        const stdVal = form.getFieldValue("std");
                                        if (stdVal) {
                                            const stdObj = stdVal && stdVal.isValid && stdVal.isValid() ? stdVal : dayjs.utc(stdVal);
                                            handleStdChange(stdObj);
                                        } else {
                                            // 没有STD时，保持STA不变，反推STD
                                            const staVal = form.getFieldValue("sta");
                                            if (staVal) {
                                                const staObj = staVal && staVal.isValid && staVal.isValid() ? staVal : dayjs.utc(staVal);
                                                handleStaChange(staObj);
                                            }
                                        }

                                        message.success(`已应用航路 ${route.route_name} 的轮档时间: ${routeBlock}分钟，并已自动调整到达`);
                                    }
                                } else {
                                    // 清除航路时，重置最小轮档时间，不强制改动实际轮档/时间
                                    form.setFieldValue("min_block_time_minutes", flight?.min_block_time_minutes || null);
                                }
                            }}
                        />
                    </Form.Item>
                    {/* <Form.Item name="ground_time_minutes" label="地面时间" tooltip="抵达后至下次起飞的地面时间">
                        <HourMinuteInput />
                    </Form.Item> */}
                </div>

                <Form.Item name="remarks" label="备注">
                    <Input.TextArea rows={2} placeholder="输入航班备注信息" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default FlightForm;
